/*
 * ZOOM AND PAN THE CANVAS (2026-10-19). Chris, looking at the flow ribbons crowded into the channel between two rows: "I am never able to hover on the ribbons that connects these 2 pages because
 * there are so many others there covering it up. I wonder if we could do an overall zoom perhaps to allow the user to zoom in to the canvas to see that connection and be able to hover on it?"
 *
 * The whole scene (boxes, bubbles, ribbons, the dock) lives in ONE <svg>, so zooming is the svg's own `viewBox`: the browser scales and clips every part of it together, and `getScreenCTM`
 * (which everything that turns a pointer position into a place on the grid already goes through) follows. It is NOT a CSS transform on purpose: the tooltips are placed from the pointer's page
 * position, which a CSS transform would have made wrong. At 1x there is NO viewBox attribute at all, so an unzoomed page is exactly what it was.
 *
 * How a person zooms (no buttons: Chris, 2026-10-19, "maybe we just have a mouse wheel and track pad only zoom for now, no controls"):
 *   - the MOUSE WHEEL over the canvas, or a trackpad PINCH or two-finger scroll over it (both arrive as wheel events): zooms about the pointer. Scrolling back out to 100% takes the zoom off.
 *   - once zoomed, DRAGGING the empty canvas moves the view, and a double-click on the empty canvas puts it back to 100%
 *   - a small label (not a control: it ignores the pointer) says how far in you are and how to get out, only while zoomed
 * One exception to "the wheel zooms": a window so small that the PAGE itself scrolls a long way (the narrow layout). A plain wheel then scrolls the page as it always did (Ctrl / Cmd + wheel, or a pinch, still zooms), and once
 * zoomed in the wheel is the canvas's whatever the window.
 *
 * This file is the arithmetic (pure, tested) and the wiring. The page-box zoom view (densityCloudService) is its own thing: this stays out of its way (`blocked`).
 */
import { createNote } from "./canvasNotes";

export const MIN_ZOOM = 1;
export const MAX_ZOOM = 5;
/** what one zoomIn() / zoomOut() call multiplies the zoom by (there are no buttons; the real-browser check and the tests drive it) */
export const ZOOM_STEP = 1.6;

/** `scale` times magnification; (vx, vy) is the top-left of the part of the scene shown, in the scene's own units */
export interface ZoomState {
  scale: number;
  vx: number;
  vy: number;
}

export const NO_ZOOM: ZoomState = { scale: 1, vx: 0, vy: 0 };

const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));

/** Keeps the zoom inside its limits and the part shown inside the scene (w x h, the svg's own size). */
export const clampZoom = (z: ZoomState, w: number, h: number): ZoomState => {
  const scale = clamp(Number.isFinite(z.scale) ? z.scale : 1, MIN_ZOOM, MAX_ZOOM);
  const vw = w / scale;
  const vh = h / scale;

  return { scale, vx: clamp(Number.isFinite(z.vx) ? z.vx : 0, 0, Math.max(0, w - vw)), vy: clamp(Number.isFinite(z.vy) ? z.vy : 0, 0, Math.max(0, h - vh)) };
};

/** Zooms by `factor` about the point (fx, fy), given as fractions (0 to 1) across the svg as it is on screen, so the scene under the pointer stays under the pointer. */
export const zoomAt = (z: ZoomState, factor: number, fx: number, fy: number, w: number, h: number): ZoomState => {
  const scale = clamp(z.scale * (Number.isFinite(factor) && factor > 0 ? factor : 1), MIN_ZOOM, MAX_ZOOM);
  if (scale === z.scale) return z;
  const ux = z.vx + fx * (w / z.scale);
  const uy = z.vy + fy * (h / z.scale);

  return clampZoom({ scale, vx: ux - fx * (w / scale), vy: uy - fy * (h / scale) }, w, h);
};

/** Drags the scene by (dxPx, dyPx) screen pixels (the scene follows the pointer); `rectW`/`rectH` are the svg's size on screen. */
export const panBy = (z: ZoomState, dxPx: number, dyPx: number, rectW: number, rectH: number, w: number, h: number): ZoomState => {
  if (!(rectW > 0) || !(rectH > 0)) return z;

  return clampZoom({ scale: z.scale, vx: z.vx - (dxPx * (w / z.scale)) / rectW, vy: z.vy - (dyPx * (h / z.scale)) / rectH }, w, h);
};

/** The svg's viewBox for a zoom of a scene whose own viewBox starts at (ox, oy), or null at 1x (nothing to set: the page is exactly as it was). */
export const viewBoxFor = (z: ZoomState, w: number, h: number, ox = 0, oy = 0): string | null => {
  if (z.scale <= MIN_ZOOM + 1e-6) return null;
  const r = (n: number) => Math.round(n * 1000) / 1000;

  return `${r(ox + z.vx)} ${r(oy + z.vy)} ${r(w / z.scale)} ${r(h / z.scale)}`;
};

/** The numbers of a viewBox attribute, or null when there is none (or it is nonsense). */
export const parseViewBox = (value: string | null): { x: number; y: number; w: number; h: number } | null => {
  if (!value) return null;
  const n = value.trim().split(/[\s,]+/).map(Number);

  return n.length === 4 && n.every(Number.isFinite) && n[2] > 0 && n[3] > 0 ? { x: n[0], y: n[1], w: n[2], h: n[3] } : null;
};

/** deltaMode 0 is pixels, 1 lines, 2 pages */
const pixelsOf = (delta: number, deltaMode: number): number => delta * (deltaMode === 1 ? 16 : deltaMode === 2 ? 400 : 1);

/**
 * How much one wheel event zooms. A trackpad pinch arrives as a stream of small deltas (a few pixels each) and a mouse wheel notch as one big one (100): the clamp makes a notch about 1.35x
 * (about three notches to double) and leaves the small steps alone.
 */
export const wheelZoomFactor = (deltaY: number, deltaMode: number): number => Math.exp(-clamp(pixelsOf(deltaY, deltaMode), -30, 30) * 0.01);

/** Whether a wheel event is an explicit ZOOM gesture (a pinch, or Ctrl / Cmd held) and not a plain wheel. */
export const isZoomGesture = (event: { ctrlKey: boolean; metaKey: boolean }): boolean => event.ctrlKey || event.metaKey;

/** A page this much taller than its window (or less) does not count as a page that scrolls: the dashboard at an ordinary laptop height overhangs by a few dozen pixels. */
export const PAGE_SCROLL_TOLERANCE = 1.25;

/**
 * Whether a PLAIN wheel (no Ctrl, no pinch) is the canvas's to zoom with. Yes when already zoomed in, and when the page has next to nothing of its own to scroll; no when the page is much taller
 * than its window (the narrow layout is taller than the screen by design), because then the wheel over the canvas is how a person moves down the page.
 */
export const plainWheelZooms = (scale: number, pageScrollHeight: number, windowHeight: number): boolean => scale > MIN_ZOOM || pageScrollHeight <= windowHeight * PAGE_SCROLL_TOLERANCE;

export const zoomLabel = (z: ZoomState): string => `${Math.round(z.scale * 100)}%`;

/** True when two zooms are the same view (a pan against the edge changes nothing). */
export const sameView = (a: ZoomState, b: ZoomState): boolean => Math.abs(a.scale - b.scale) < 1e-9 && Math.abs(a.vx - b.vx) < 1e-6 && Math.abs(a.vy - b.vy) < 1e-6;

// ── the wiring ─────────────────────────────────────────────────────────────────────────────

export interface CanvasZoomOptions {
  /** true while something else owns the canvas (the page-box zoom view, the single view): the wheel and the drag are left alone */
  blocked?: () => boolean;
  /** told after every change */
  onChange?: (z: ZoomState) => void;
}

export interface CanvasZoom {
  /** stops listening, removes the label and puts the svg back as it was */
  destroy: () => void;
  reset: () => void;
  zoomIn: () => void;
  zoomOut: () => void;
  state: () => ZoomState;
}

/**
 * The scene as the app itself shows it: its own viewBox (the app sets one to scale the grid down to a narrow window, "scale to fit", and rebuilds it whenever the window changes) or, with
 * none, the svg's own size. The zoom is a window onto THAT, so it composes with the app's scaling instead of replacing it.
 */
const sceneOf = (svg: SVGSVGElement): { x: number; y: number; w: number; h: number } => {
  const own = parseViewBox(svg.getAttribute("viewBox"));
  if (own) return own;
  const rect = svg.getBoundingClientRect();

  return { x: 0, y: 0, w: Number(svg.getAttribute("width")) || rect.width, h: Number(svg.getAttribute("height")) || rect.height };
};

/** The empty canvas (the grey stage, or the svg itself where there is no stage): dragging or double-clicking anywhere else (a box, a bubble, a ribbon, a label) does its own thing. */
const isBackground = (svg: SVGSVGElement, target: EventTarget | null): boolean => target === svg || (target instanceof Element && target.id === "maincluster");

export const installCanvasZoom = (svg: SVGSVGElement, options: CanvasZoomOptions = {}): CanvasZoom => {
  let zoom: ZoomState = NO_ZOOM;
  let drag: { x: number; y: number } | null = null;
  const previousOverflow = svg.style.overflow;

  // the note: where the zoom stands and how to leave it. Not a control (it ignores the pointer), and only there while zoomed.
  const note = createNote(svg, "canvas-zoom-label");

  // What the app had when the zoom left 100% (to give back exactly), and what the zoom itself last wrote (to tell its own changes from the app's).
  let scene = { x: 0, y: 0, w: 0, h: 0 };
  let appViewBox: string | null = null;
  let wrote: { viewBox: string; size: string } | null = null;
  const sizeOf = () => `${svg.getAttribute("width")}x${svg.getAttribute("height")}`;

  const leaveZoom = () => {
    zoom = NO_ZOOM;
    wrote = null;
    svg.style.overflow = previousOverflow || "visible";
    svg.classList.remove("canvas-zoomed", "canvas-panning");
    note.hide();
  };

  const apply = (next: ZoomState) => {
    if (zoom.scale <= MIN_ZOOM && next.scale > MIN_ZOOM) {
      appViewBox = svg.getAttribute("viewBox");
      scene = sceneOf(svg);
    }
    zoom = clampZoom(next, scene.w, scene.h);
    const box = zoom.scale > MIN_ZOOM ? viewBoxFor(zoom, scene.w, scene.h, scene.x, scene.y) : null;
    if (box) {
      svg.setAttribute("viewBox", box);
      wrote = { viewBox: box, size: sizeOf() };
      // an svg shows what spills outside it (a bubble is born left of the canvas, in the Real Time list); zoomed in, that would draw the whole scene over the page, so it is clipped
      svg.style.overflow = "hidden";
      svg.classList.add("canvas-zoomed");
      note.show(`${zoomLabel(zoom)} · double-click the empty canvas to reset`);
    } else {
      // back at 100%: the app's own viewBox, exactly as it was (or none)
      if (wrote) {
        if (appViewBox) svg.setAttribute("viewBox", appViewBox);
        else svg.removeAttribute("viewBox");
      }
      leaveZoom();
    }
    options.onChange?.(zoom);
  };

  const fractionsAt = (clientX: number, clientY: number) => {
    const rect = svg.getBoundingClientRect();

    return { fx: clamp((clientX - rect.left) / (rect.width || 1), 0, 1), fy: clamp((clientY - rect.top) / (rect.height || 1), 0, 1) };
  };
  const zoomBy = (factor: number, clientX?: number, clientY?: number) => {
    const { fx, fy } = clientX === undefined || clientY === undefined ? { fx: 0.5, fy: 0.5 } : fractionsAt(clientX, clientY);
    const { w, h } = zoom.scale > MIN_ZOOM ? scene : sceneOf(svg);
    apply(zoomAt(zoom.scale > MIN_ZOOM ? zoom : { ...NO_ZOOM }, factor, fx, fy, w, h));
  };
  const reset = () => apply(NO_ZOOM);
  const panScreen = (dx: number, dy: number) => {
    const rect = svg.getBoundingClientRect();
    const next = panBy(zoom, dx, dy, rect.width, rect.height, scene.w, scene.h);
    if (!sameView(next, zoom)) apply(next);
  };

  const onWheel = (event: WheelEvent) => {
    if (options.blocked?.()) return;
    const explicit = isZoomGesture(event);
    if (!explicit && !plainWheelZooms(zoom.scale, document.documentElement.scrollHeight, window.innerHeight)) return;
    // ours, so the page neither scrolls nor (for a pinch, which the browser would turn into a zoom of the WHOLE PAGE) zooms
    event.preventDefault();
    const factor = wheelZoomFactor(event.deltaY, event.deltaMode);
    if ((zoom.scale <= MIN_ZOOM && factor < 1) || (zoom.scale >= MAX_ZOOM && factor > 1)) return;
    zoomBy(factor, event.clientX, event.clientY);
  };
  const onDown = (event: MouseEvent) => {
    if (event.button !== 0 || zoom.scale <= MIN_ZOOM || options.blocked?.() || !isBackground(svg, event.target)) return;
    drag = { x: event.clientX, y: event.clientY };
    svg.classList.add("canvas-panning");
    event.preventDefault();
  };
  const onMove = (event: MouseEvent) => {
    if (!drag) return;
    panScreen(event.clientX - drag.x, event.clientY - drag.y);
    drag = { x: event.clientX, y: event.clientY };
  };
  const onUp = () => {
    drag = null;
    svg.classList.remove("canvas-panning");
  };
  const onDouble = (event: MouseEvent) => {
    if (zoom.scale > MIN_ZOOM && isBackground(svg, event.target)) reset();
  };
  // The app rebuilds the scene when the window changes (new width, new height, a new viewBox of its own): a view kept across that would show the wrong part, and the app's new viewBox must
  // be left exactly as it set it. So anything on the svg that the zoom did not write itself ENDS the zoom, without touching the svg.
  const mutations = typeof MutationObserver === "function"
    ? new MutationObserver(() => {
        if (zoom.scale <= MIN_ZOOM || !wrote) return;
        if (svg.getAttribute("viewBox") === wrote.viewBox && sizeOf() === wrote.size) return;
        leaveZoom();
        options.onChange?.(zoom);
      })
    : null;
  svg.addEventListener("wheel", onWheel, { passive: false });
  svg.addEventListener("mousedown", onDown);
  svg.addEventListener("dblclick", onDouble);
  window.addEventListener("mousemove", onMove);
  window.addEventListener("mouseup", onUp);
  mutations?.observe(svg, { attributes: true, attributeFilter: ["viewBox", "width", "height"] });
  apply(NO_ZOOM);

  return {
    destroy: () => {
      svg.removeEventListener("wheel", onWheel);
      svg.removeEventListener("mousedown", onDown);
      svg.removeEventListener("dblclick", onDouble);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      mutations?.disconnect();
      if (zoom.scale > MIN_ZOOM && wrote) {
        if (appViewBox) svg.setAttribute("viewBox", appViewBox);
        else svg.removeAttribute("viewBox");
      }
      svg.style.overflow = previousOverflow;
      svg.classList.remove("canvas-zoomed", "canvas-panning");
      note.remove();
    },
    reset,
    zoomIn: () => zoomBy(ZOOM_STEP),
    zoomOut: () => zoomBy(1 / ZOOM_STEP),
    state: () => zoom,
  };
};
